import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, XCircle, Award, Building2, Sparkles, Target, MapPin, GraduationCap, Compass, TrendingUp, ExternalLink, Users, Calendar, ChevronDown, Loader2, ArrowUpRight } from "lucide-react";

import { useI18n } from "@/i18n";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, track } from "@/lib/api";
/* Parse an upper bound salary from strings like "42.000 – 51.000 €" */
function parseMaxSalary(range, lang) {
  if (!range || typeof range !== "string") return null;
  const nums = range.replace(/\./g, "").match(/\d[\d\s]*/g);
  if (!nums || nums.length === 0) return null;
  const parsed = nums.map(n => parseInt(n.replace(/\s/g, ""), 10)).filter(Boolean);
  if (parsed.length === 0) return null;
  const max = Math.max(...parsed);
  return lang === "en"
    ? "€" + max.toLocaleString("en-US")
    : max.toLocaleString("de-DE") + " €";
}

export default function Report() {
  const { token } = useParams();
  const { t, lang } = useI18n();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [rerouting, setRerouting] = useState(null); // career_path_id being loaded
  const [explanationLoading, setExplanationLoading] = useState(false);

  useEffect(() => {
    api.get(`/report/${token}`).then(res => setR(res.data)).catch(e => setErr(e?.response?.status || "error"));
    api.get(`/config/public`).then(res => setBookingUrl(res.data?.booking_url || "")).catch(() => {});
  }, [token]);

  // v2.13 — Locale switch: re-fetch (or generate + cache) the AI explanation in the target language.
  // Never show a mixed-language view — while loading, show a localized loading state.
  useEffect(() => {
    if (!r) return;
    const cached = (r.explanations_by_locale || {})[lang];
    if (cached) {
      if (r.explanation !== cached) setR(prev => ({ ...prev, explanation: cached }));
      return;
    }
    if (r.lang === lang && r.explanation) return; // initial locale matches
    let cancelled = false;
    setExplanationLoading(true);
    api.post(`/report/${token}/explain`, { lang })
      .then(res => {
        if (cancelled) return;
        setR(prev => ({
          ...prev,
          explanation: res.data.explanation,
          explanations_by_locale: { ...(prev.explanations_by_locale || {}), [lang]: res.data.explanation },
          lang,
        }));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setExplanationLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, r?._id || token]);

  if (err) return <Fallback msg={`Report not found (${err})`} />;
  if (!r) return <Fallback msg={lang === "de" ? "Wird geladen…" : "Loading…"} />;

  const p = r.profile || {};
  const mr = r.match_result || {};
  const top = mr.top_recommendation || {};
  const role = top.job_role || {};
  const cert = mr.certification || {};
  const course = mr.course || {};
  const courseFallback = mr.course_fallback;
  const courseHasLiveUrl = course && course.landing_url_status === "verified_live" && course.landing_url;
  const prepCourses = mr.prep_courses || [];
  const roadmap8 = mr.roadmap_8_weeks;
  const levelMilestones = mr.level_milestones;
  const employers = mr.employer_examples || [];
  const alt = mr.alternatives || [];
  const sa = mr.skill_analysis || {};
  const expl = r.explanation || {};

  // v2.10 — reference-only paths (no live product) render the waitlist module + related live-path alternatives
  // v2.17 — course_product presence overrides has_live_course=false (products are always live sellable offers)
  // v2.18 — hasProducts also overrides is_reference_only so the product card + prep-courses section render for SF/etc.
  const hasProducts = (top.products || []).length > 0;
  const isReferenceOnly = !!top.is_reference_only && !hasProducts;
  const hasLiveCourse = hasProducts || (top.has_live_course !== false && (course && (course.kajabi_trial_offer_url || courseHasLiveUrl)));
  const relatedLivePaths = mr.related_live_paths || [];

  const maxSalary = parseMaxSalary(role.salary_range, lang);
  const firstName = (p.first_name || "").trim();
  // v2.13 — locale-aware field selection
  const pickLocale = (de, en) => (lang === "en" ? (en || de) : (de || en));
  const roleTitle = pickLocale(role.title_de, role.title_en) || top.career_path_name;
  const rerouted = !!mr.rerouted;

  function openBooking(source) {
    track("header_cta_clicked", null, { variant: source });
    setBookingOpen(true);
  }
  function startFree(source) {
    track("header_cta_clicked", null, { variant: source, action: "start_free" });
    // "Start for free" → scroll to course card if live, otherwise open booking
    if (courseHasLiveUrl) {
      document.getElementById("course-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      openBooking(source);
    }
  }
  function confirmBooking() {
    track("booking_link_clicked", null, { consent, report_id: token });
    const params = new URLSearchParams();
    if (consent) {
      params.set("report_id", token);
      params.set("utm_source", "advisor");
      params.set("utm_campaign", top.career_path_id || "");
      // Calendly custom-question style prefill
      params.set("a1", token);
    }
    const url = bookingUrl + (bookingUrl.includes("?") ? "&" : "?") + params.toString();
    window.open(url, "_blank", "noopener,noreferrer");
    setBookingOpen(false);
  }

  async function rerouteToPath(cpId) {
    if (!cpId || cpId === top.career_path_id) return;
    setRerouting(cpId);
    track("alternative_path_clicked", null, { career_path_id: cpId });
    try {
      // Fast: deterministic reroute
      const { data } = await api.post(`/report/${token}/reroute`, { career_path_id: cpId });
      setR(prev => ({ ...prev, match_result: data.match_result, explanation: data.explanation }));
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Slow (async): regenerate AI explanation for the new path
      if (data.explanation_stale) {
        setExplanationLoading(true);
        try {
          const { data: exp } = await api.post(`/report/${token}/reroute-explain`, { career_path_id: cpId });
          setR(prev => ({ ...prev, explanation: exp.explanation }));
        } catch { /* keep stale */ } finally {
          setExplanationLoading(false);
        }
      }
    } catch {
      /* ignore */
    } finally { setRerouting(null); }
  }

  return (
    <div className="min-h-screen">
      <Brand />

      {/* Header — sticky conversion zone */}
      <section className="border-b border-border/60 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="mx-auto max-w-6xl px-6 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-accent">
              <Sparkles className="h-3 w-3"/> {t("report_kicker")}
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              {firstName
                ? <><span>{t("hello")} {firstName} — </span><span className="text-muted-foreground/70 text-3xl sm:text-4xl">{t("headline_way_to")} </span>{roleTitle}</>
                : roleTitle}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Compass className="h-4 w-4"/>{top.career_path_name}</span>
              <span className="inline-flex items-center gap-1.5"><TrendingUp className="h-4 w-4"/>{top.level}</span>
              {maxSalary ? (
                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground" data-testid="header-salary">
                  {t("salary_upto")} {maxSalary}
                </span>
              ) : role.salary_range === null ? (
                <span className="italic text-muted-foreground/80">{t("salary_on_request")}</span>
              ) : null}
            </div>

            {/* CTAs — above the fold */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button size="lg" data-testid="cta-free-header" onClick={() => startFree("header")}
                      className="rounded-full px-6 h-11 text-sm font-semibold">
                {t("cta_free_start")} →
              </Button>
              <Button variant="outline" size="lg" data-testid="cta-booking-header" onClick={() => openBooking("header")}
                      className="rounded-full px-6 h-11 text-sm font-semibold">
                <Calendar className="h-4 w-4 mr-2"/>{t("cta_book_call")}
              </Button>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">{t("free_line")}</div>
          </div>
          <div className="lg:col-span-4 flex items-center justify-center lg:justify-end">
            <RadialScore value={top.match_score ?? 0} label={t("match_score")} confidence={mr.confidence}/>
          </div>
        </div>
      </section>

      {rerouting && (
        <div className="mx-auto max-w-6xl px-6 pt-4 text-xs text-muted-foreground inline-flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin"/> {t("alt_reroute_loading")}
        </div>
      )}

      <main className="mx-auto max-w-6xl px-6 py-12 space-y-10">
        {/* Profile summary */}
        <BlockGrid>
          <Card title={t("section_profile")} icon={Building2} colSpan={12} testid="s-profile">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Fact label={t("role_short")} value={p.current_position}/>
              <Fact label={t("industry")} value={p.industry}/>
              <Fact label={t("experience")} value={p.years_experience ? `${p.years_experience}+ ${lang === "de" ? "Jahre" : "years"}` : "—"}/>
              <Fact label="SAP" value={p.sap_experience}/>
              <Fact label={t("education")} value={p.education}/>
              <Fact label={t("field_location")} value={p.location || "—"} icon={MapPin}/>
              <Fact label={t("languages")} value={(p.languages || []).join(", ") || "—"}/>
              <Fact label={t("goal")} value={p.career_goal || "—"}/>
            </div>
          </Card>
        </BlockGrid>

        {/* v2.7 — Fixed 4-tile salary module */}
        <SalaryTiles tiles={mr.salary_tiles || []} t={t} lang={lang}/>

        {/* Legacy progression (kept for compat when salary_tiles empty) */}
        {(!mr.salary_tiles || mr.salary_tiles.length === 0) && (
          <SalaryProgression steps={mr.progression || []} t={t} lang={lang}/>
        )}

        {/* Why + Next role */}
        <BlockGrid>
          <Card title={t("why_fits")} icon={Sparkles} colSpan={8} className="ai-glow" testid="s-why">
            {explanationLoading ? (
              <div className="space-y-3">
                <div className="h-4 rounded bg-muted animate-pulse w-11/12"/>
                <div className="h-4 rounded bg-muted animate-pulse w-4/5"/>
                <div className="h-4 rounded bg-muted animate-pulse w-3/4"/>
                <div className="text-xs text-muted-foreground inline-flex items-center gap-2 mt-2">
                  <Loader2 className="h-3 w-3 animate-spin"/>{lang === "de" ? "Neue Erklärung wird generiert…" : "Generating fresh explanation…"}
                </div>
              </div>
            ) : (
              <p className="text-base md:text-lg leading-relaxed">{expl.why_this_fits || "—"}</p>
            )}
            {rerouted ? null : <ScoreBreakdown breakdown={top.breakdown || {}} lang={lang} t={t}/>}
          </Card>
          <Card title={t("section_role")} icon={Target} colSpan={4} testid="s-role">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("next_role_label")}</div>
            <div className="mt-1 text-lg font-semibold">{pickLocale(role.title_de, role.title_en) || "—"}</div>
            {role.typical_tasks && <p className="mt-3 text-sm text-muted-foreground line-clamp-5">{pickLocale(role.typical_tasks_de, role.typical_tasks_en) || role.typical_tasks}</p>}
            {role.next_role_title && (
              <div className="mt-4 text-xs">
                <span className="text-muted-foreground uppercase tracking-widest">{t("then")}: </span>
                <span className="font-medium">{pickLocale(role.next_role_title_de, role.next_role_title_en) || role.next_role_title}</span>
              </div>
            )}
            {role.salary_range && <div className="mt-3 text-xs text-muted-foreground">{role.salary_range}</div>}
          </Card>
        </BlockGrid>

        {/* Skills */}
        <BlockGrid>
          <Card title={t("section_skills")} icon={GraduationCap} colSpan={12} testid="s-skills">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <SkillGroup title={t("strong")} icon={CheckCircle2} tone="emerald" items={sa.already_strong} />
              <SkillGroup title={t("partial")} icon={AlertCircle} tone="amber" items={sa.partially_covered} />
              <SkillGroup title={t("gap")} icon={XCircle} tone="rose" items={sa.gap} />
            </div>
          </Card>
        </BlockGrid>

        {/* Roadmap — 8-week fixed from certification data. v2.10: omitted for reference-only paths; show cert topics instead. */}
        {isReferenceOnly && cert && (cert.topics || []).length > 0 ? (
          <BlockGrid>
            <Card title={lang === "de" ? "Prüfungsthemen zur Orientierung" : "Exam topics for orientation"} icon={Compass} colSpan={12} testid="s-cert-topics">
              <div className="mb-3 text-xs text-muted-foreground">
                {lang === "de" ? "Diese Themen sind Bestandteil der offiziellen Zertifizierung " : "These topics are part of the official certification "}
                <span className="font-medium text-foreground">{cert.exam_code || cert.name}</span>.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(cert.topics || []).map((topic, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`cert-topic-${i}`}>
                    <span className="text-primary mt-0.5">›</span>
                    <span>{typeof topic === "string" ? topic : (topic[lang] || topic.de || topic.en || topic.name)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Button variant="outline" data-testid="cta-booking-roadmap"
                        onClick={() => { track("booking_link_clicked", null, { source: "cert_topics" }); openBooking("cert_topics"); }}
                        className="rounded-full">
                  <Calendar className="h-4 w-4 mr-2"/>{t("cta_book_ctx")}
                </Button>
              </div>
            </Card>
          </BlockGrid>
        ) : roadmap8 && roadmap8.phases && roadmap8.phases.length > 0 ? (
          <BlockGrid>
            <Card title={t("section_roadmap")} icon={Compass} colSpan={12} testid="s-roadmap">
              <div className="mb-4 text-sm">
                <span className="text-muted-foreground">{t("roadmap_total_prefix")}</span>{" "}
                <span className="font-semibold">{roadmap8.total_weeks} {t("roadmap_total_weeks")}</span>
              </div>
              <Roadmap8 phases={roadmap8.phases} lang={lang}/>
              <div className="mt-6">
                <Button variant="outline" data-testid="cta-booking-roadmap"
                        onClick={() => { track("booking_link_clicked", null, { source: "roadmap" }); openBooking("roadmap"); }}
                        className="rounded-full">
                  <Calendar className="h-4 w-4 mr-2"/>{t("cta_book_ctx")}
                </Button>
              </div>
            </Card>
          </BlockGrid>
        ) : (
          <BlockGrid>
            <Card title={t("section_roadmap")} icon={Compass} colSpan={12} testid="s-roadmap">
              <RoadmapAccordion steps={expl.roadmap || []} t={t}/>
              <div className="mt-6">
                <Button variant="outline" data-testid="cta-booking-roadmap"
                        onClick={() => { track("booking_link_clicked", null, { source: "roadmap" }); openBooking("roadmap"); }}
                        className="rounded-full">
                  <Calendar className="h-4 w-4 mr-2"/>{t("cta_book_ctx")}
                </Button>
              </div>
            </Card>
          </BlockGrid>
        )}

        {/* Level milestones — partial completion offer */}
        {levelMilestones && levelMilestones.milestones && (
          <BlockGrid>
            <Card title={lang === "de" ? "Dein Fortschritt in Stufen" : "Your progress in stages"} icon={TrendingUp} colSpan={12} testid="s-milestones">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {["anwender_sachbearbeiter", "key_user", "consultant_full_certification"].map(key => {
                  const m = levelMilestones.milestones[key];
                  if (!m) return null;
                  const labels = {
                    anwender_sachbearbeiter: lang === "de" ? "Anwender / Sachbearbeiter" : "User / Clerk",
                    key_user: "Key User",
                    consultant_full_certification: lang === "de" ? "Volle Zertifizierung" : "Full certification",
                  };
                  return (
                    <div key={key} className="rounded-xl border border-border bg-card p-4">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-accent">{labels[key]}</div>
                      <div className="mt-2 text-2xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{m.unit_count} Units</div>
                      <div className="text-xs text-muted-foreground mt-1">~{Math.round(m.approx_duration_min)} min</div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </BlockGrid>
        )}

        {/* Cert + Course */}
        <BlockGrid>
          <Card title={t("section_cert")} icon={Award} colSpan={5} testid="s-cert">
            <div className="text-lg font-semibold">{cert.name || "—"}</div>
            <div className="mt-1 flex flex-wrap gap-2 text-xs">
              {cert.exam_code && <Tag>{cert.exam_code}</Tag>}
              {cert.deployment && <Tag>{cert.deployment}</Tag>}
              {cert.level && <Tag>{cert.level}</Tag>}
              {cert.verification === "unverified" && <Tag>unverified</Tag>}
            </div>
            {cert.topics && cert.topics.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">{t("exam_topics")}</div>
                <ul className="text-xs space-y-1 max-h-40 overflow-auto pr-2">
                  {cert.topics.slice(0, 8).map((tp, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span className="truncate">{tp.name}</span>
                      {tp.weighting && <span className="text-muted-foreground shrink-0">{tp.weighting}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          <Card title={t("section_course")} icon={GraduationCap} colSpan={7} testid="s-course" className={hasLiveCourse ? "ai-glow" : ""}>
            <div id="course-section" />
            {isReferenceOnly || !hasLiveCourse ? (
              <WaitlistModule
                careerPathId={top.career_path_id}
                careerPathName={top.career_path_name}
                cert={cert}
                relatedLivePaths={relatedLivePaths}
                sessionId={r.session_id}
                firstName={p.first_name}
                token={token}
                openBooking={openBooking}
                t={t} lang={lang}
              />
            ) : (
              <ProductCourseCard
                course={course}
                cert={cert}
                prepCourses={prepCourses}
                levelMilestones={levelMilestones}
                careerPathId={top.career_path_id}
                products={top.products || []}
                token={token}
                courseHasLiveUrl={courseHasLiveUrl}
                openBooking={openBooking}
                t={t} lang={lang}
              />
            )}
          </Card>
        </BlockGrid>

        {/* Deine Zertifizierungsvorbereitung — all courses preparing for the chosen certification (skipped for reference-only paths) */}
        {!isReferenceOnly && prepCourses.length > 0 && (
          <BlockGrid>
            <Card title={lang === "de" ? "Deine Zertifizierungsvorbereitung" : "Your certification preparation"} icon={GraduationCap} colSpan={12} testid="s-prep-courses">
              <div className="text-xs text-muted-foreground mb-4">
                {lang === "de" ? "Bereitet vor auf: " : "Prepares for: "}
                <span className="font-medium text-foreground">{(cert && cert.name) || "—"}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {prepCourses.map((c, i) => {
                  const live = c.landing_url_status === "verified_live" && c.landing_url;
                  return (
                    <div key={c.id || i} data-testid={`prep-course-${i}`} className="rounded-xl border border-border bg-card p-4">
                      <div className="text-sm font-semibold">{c.title}</div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs">
                        <Tag>{c.duration_min ? `${Math.round(c.duration_min)} min` : (lang === "de" ? "Kompaktkurs" : "Compact")}</Tag>
                        {c.also_prepares_for && c.also_prepares_for.length > 0 && <Tag>{lang === "de" ? "auch für weitere Zertifizierungen" : "also for other certs"}</Tag>}
                      </div>
                      <div className="mt-3 flex gap-2 flex-wrap">
                        {live ? (
                          <a href={c.landing_url + `?utm_source=career_advisor&utm_campaign=${encodeURIComponent(top.career_path_id || "")}`}
                             target="_blank" rel="noreferrer"
                             className="text-xs font-semibold text-primary hover:underline">
                            {lang === "de" ? "Kurs ansehen" : "View course"} →
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">{lang === "de" ? "Landing-Seite in Vorbereitung" : "Landing page in prep"}</span>
                        )}
                        {c.kajabi_trial_offer_url && (
                          <a href={c.kajabi_trial_offer_url} target="_blank" rel="noreferrer"
                             className="text-xs font-semibold text-accent hover:underline">
                            {t("open_course")}
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </BlockGrid>
        )}

        {/* Employers + Alternatives */}
        <BlockGrid>
          <Card title={t("section_employers")} icon={Users} colSpan={8} testid="s-employers">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {employers.map((e, i) => (
                <motion.div key={e.id || i}
                  initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="rounded-xl border border-border bg-card p-4 hover:-translate-y-0.5 transition-transform">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-accent">{e.level}{e.entry_type === "consultancy" ? " · Beratung" : ""}</div>
                  <div className="mt-1 font-semibold">{e.company_name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{e.industry}</div>
                  <div className="text-xs text-muted-foreground">{e.typical_location}</div>
                </motion.div>
              ))}
              {employers.length === 0 && <div className="text-sm text-muted-foreground">—</div>}
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("employers_note")}</div>
          </Card>

          <Card title={t("section_alt")} icon={Compass} colSpan={4} testid="s-alt">
            <div className="text-[10px] text-muted-foreground mb-3">{t("alt_click_hint")}</div>
            <ul className="space-y-3">
              {alt.map((a, i) => {
                const hint = lang === "de" ? a.relevance_hint_de : (a.relevance_hint_en || a.relevance_hint_de);
                const utmLanding = a.landing_url_status === "verified_live" && a.landing_url
                  ? a.landing_url + `?utm_source=career_advisor&utm_campaign=${encodeURIComponent(a.career_path_id)}&utm_content=alternative`
                  : null;
                return (
                  <li key={i} className="rounded-lg border border-border p-3">
                    <button
                      data-testid={`alt-${a.career_path_id}`}
                      onClick={() => rerouteToPath(a.career_path_id)}
                      disabled={!!rerouting}
                      className="w-full text-left disabled:opacity-50">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-medium truncate">{a.career_path_name}</div>
                        <div className="text-xs text-muted-foreground shrink-0">{Math.round(a.display_score ?? (a.score * 100))}%</div>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: `${Math.round(a.display_score ?? a.score * 100)}%` }} />
                      </div>
                      {hint && <div className="mt-2 text-xs text-muted-foreground">{hint}</div>}
                      {a.product_name && (
                        <div className="mt-2 text-[11px] leading-snug text-foreground/80">
                          <span className="uppercase tracking-widest text-[9px] text-accent">{lang === "de" ? "Kurs" : "Course"}:</span>{" "}
                          {a.product_name}
                        </div>
                      )}
                    </button>
                    {a.kajabi_trial_offer_url && (
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <a href={a.kajabi_trial_offer_url} target="_blank" rel="noreferrer"
                           onClick={() => { track("course_clicked", null, { source: "alternative", career_path_id: a.career_path_id }); api.post(`/report/${token}/course-click`).catch(() => {}); }}
                           data-testid={`alt-trial-${a.career_path_id}`}
                           className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-[11px] font-semibold hover:opacity-90 transition-opacity">
                          {lang === "de" ? "Kurs kostenlos testen" : "Try course free"} <ExternalLink className="h-3 w-3"/>
                        </a>
                        {utmLanding && (
                          <a href={utmLanding} target="_blank" rel="noreferrer"
                             data-testid={`alt-landing-${a.career_path_id}`}
                             className="text-[11px] text-primary hover:underline underline-offset-4">
                            {lang === "de" ? "Details" : "Details"} →
                          </a>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
              {alt.length === 0 && <li className="text-sm text-muted-foreground">—</li>}
            </ul>
          </Card>
        </BlockGrid>

        {/* Bottom CTAs */}
        <section className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-transparent to-accent/5 p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
            {lang === "de" ? "Bereit für den nächsten Schritt?" : "Ready for the next step?"}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("free_line")}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button size="lg" data-testid="cta-free-bottom" onClick={() => startFree("bottom")}
                    className="rounded-full px-6 h-11 text-sm font-semibold">
              {t("cta_free_start")} →
            </Button>
            <Button variant="outline" size="lg" data-testid="cta-booking-bottom" onClick={() => openBooking("bottom")}
                    className="rounded-full px-6 h-11 text-sm font-semibold">
              <Calendar className="h-4 w-4 mr-2"/>{t("cta_book_call")}
            </Button>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="rounded-xl border border-border bg-muted/40 p-5">
          <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">{t("disclaimer")}</div>
          <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{expl.disclaimer}</p>
        </div>

        {/* v2.14 — Report feedback thumbs */}
        <FeedbackThumbs token={token} lang={lang} initial={r.feedback}/>
      </main>

      {/* Booking modal */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calendar className="h-5 w-5"/>{t("booking_modal_title")}</DialogTitle>
            <DialogDescription>{t("booking_modal_desc")}</DialogDescription>
          </DialogHeader>
          {!bookingUrl ? (
            <div className="text-sm text-muted-foreground">{t("booking_no_url")}</div>
          ) : (
            <>
              <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer">
                <Checkbox data-testid="booking-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5"/>
                <div>
                  <div className="text-sm">{t("consent_label")}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{t("consent_hint")}</div>
                </div>
              </label>
              <DialogFooter className="mt-4">
                <Button variant="outline" data-testid="booking-skip" onClick={() => { setConsent(false); confirmBooking(); }}>{t("booking_skip")}</Button>
                <Button data-testid="booking-continue" onClick={confirmBooking}>{t("booking_continue")} <ExternalLink className="h-4 w-4 ml-2"/></Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ helpers ============ */

function BlockGrid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-12 gap-6">{children}</div>;
}

function Card({ title, icon: Icon, colSpan = 6, className = "", children, testid }) {
  return (
    <motion.section
      data-testid={testid}
      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35 }}
      className={`col-span-12 md:col-span-${colSpan} rounded-2xl border border-border bg-card p-6 ${className}`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5"/>}
        <span>{title}</span>
      </div>
      <div className="mt-4">{children}</div>
    </motion.section>
  );
}

function Fact({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground"/>} {value || "—"}
      </div>
    </div>
  );
}

function Tag({ children }) {
  return <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[11px] text-muted-foreground">{children}</span>;
}

function SkillGroup({ title, icon: Icon, tone, items }) {
  const toneMap = {
    emerald: { border: "border-emerald-500/30", bg: "bg-emerald-500/5", text: "text-emerald-700 dark:text-emerald-300", icon: "text-emerald-600 dark:text-emerald-400" },
    amber:   { border: "border-amber-500/30",   bg: "bg-amber-500/5",   text: "text-amber-700 dark:text-amber-300",   icon: "text-amber-600 dark:text-amber-400" },
    rose:    { border: "border-rose-500/30",    bg: "bg-rose-500/5",    text: "text-rose-700 dark:text-rose-300",     icon: "text-rose-600 dark:text-rose-400" },
  }[tone];
  return (
    <div className={`rounded-xl border ${toneMap.border} ${toneMap.bg} p-4`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${toneMap.icon}`}/>
        <div className={`text-sm font-semibold ${toneMap.text}`}>{title}</div>
        <div className="ml-auto text-xs text-muted-foreground">{(items || []).length}</div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {(items || []).slice(0, 20).map((s, i) => (
          <span key={i} className={`inline-flex items-center rounded-full border ${toneMap.border} bg-card px-2.5 py-1 text-xs`}>
            {s.canonical_skill || s}
          </span>
        ))}
        {(!items || items.length === 0) && <span className="text-xs text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function ScoreBreakdown({ breakdown, lang, t }) {
  const labels = {
    task_match: t("tasks"), skill_match: lang === "de" ? "Skills" : "Skills",
    profession_prior: lang === "de" ? "Beruf-Prior" : "Prof. prior",
    industry_match: t("industry"), sap_experience: "SAP",
    career_goal: t("goal"), work_preference: lang === "de" ? "Präferenz" : "Preference",
  };
  return (
    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
      {Object.entries(breakdown).map(([k, v]) => (
        <div key={k}>
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{labels[k] || k}</span>
            <span className="text-xs font-semibold">{Math.round((v || 0) * 100)}</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
            <motion.div initial={{ width: 0 }} whileInView={{ width: `${Math.round((v || 0) * 100)}%` }} viewport={{ once: true }}
              transition={{ duration: 0.7 }} className="h-full bg-primary"/>
          </div>
        </div>
      ))}
    </div>
  );
}

function RadialScore({ value, label, confidence }) {
  const size = 180, stroke = 12;
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const dash = Math.max(0, Math.min(1, value / 100)) * c;
  return (
    <div className="relative grid place-items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} stroke="hsl(var(--muted))" strokeWidth={stroke} fill="none"/>
        <motion.circle
          cx={size/2} cy={size/2} r={radius} stroke="hsl(var(--primary))" strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - dash }}
          transition={{ duration: 1.1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="text-4xl font-semibold tabular-nums" data-testid="report-score" style={{ fontFamily: 'var(--font-heading)' }}>{value}%</div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.24em] text-muted-foreground">{label}</div>
          {confidence && (
            <div className="mt-2 inline-flex items-center rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-accent">
              {confidence}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Condensed accordion roadmap — collapsed by default, max 5 visible phases */
function RoadmapAccordion({ steps, t }) {
  const merged = useMemo(() => {
    const arr = Array.isArray(steps) ? [...steps] : [];
    // condense to max 5 phases by merging adjacent extras
    if (arr.length <= 5) return arr;
    const out = arr.slice(0, 4);
    const rest = arr.slice(4);
    out.push({
      week_range: rest[0]?.week_range?.split("-")[0] + "+",
      focus: rest.map(r => r.focus).filter(Boolean).join(" · "),
      activities: rest.flatMap(r => r.activities || []),
    });
    return out;
  }, [steps]);

  const totalWeeks = useMemo(() => {
    let max = 0;
    for (const s of steps || []) {
      const m = String(s.week_range || "").match(/(\d+)/g);
      if (m) max = Math.max(max, ...m.map(x => parseInt(x, 10)).filter(x => !isNaN(x)));
    }
    return max;
  }, [steps]);

  const [open, setOpen] = useState(null);

  if (!merged || merged.length === 0) return <div className="text-sm text-muted-foreground">—</div>;

  return (
    <>
      {totalWeeks > 0 && (
        <div className="mb-4 text-sm">
          <span className="text-muted-foreground">{t("roadmap_total_prefix")}</span>{" "}
          <span className="font-semibold">ca. {totalWeeks} {t("roadmap_total_weeks")}</span>
        </div>
      )}
      <div className="rounded-xl border border-border overflow-hidden">
        {merged.map((step, i) => {
          const isOpen = open === i;
          const summary = (step.activities && step.activities[0]) || "";
          return (
            <div key={i} className="border-b border-border last:border-b-0">
              <button
                data-testid={`roadmap-toggle-${i}`}
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-muted/40 transition-colors">
                <div className="shrink-0 w-16 text-[10px] uppercase tracking-widest text-accent">{step.week_range}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{step.focus}</div>
                  {!isOpen && summary && (
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-1">{summary}</div>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}/>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden">
                    <ul className="px-5 pb-4 pl-[104px] text-sm text-muted-foreground space-y-1.5">
                      {(step.activities || []).map((a, k) => (
                        <li key={k} className="flex gap-2"><span className="text-primary">›</span> {a}</li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* v2.10 — Waitlist module for career paths without a live ERP Institute course */
function WaitlistModule({ careerPathId, careerPathName, cert, relatedLivePaths, sessionId, firstName, token, openBooking, t, lang }) {
  const [email, setEmail] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/waitlist/join", {
        email: email.trim(),
        career_path_id: careerPathId,
        session_id: sessionId,
        first_name: firstName,
        lang,
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true); // fail silently to not block UX
    } finally { setLoading(false); }
  };
  return (
    <div className="space-y-5" data-testid="waitlist-module">
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-5">
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-card px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-primary">
          <Sparkles className="h-3 w-3"/> {lang === "de" ? "In Planung" : "Coming soon"}
        </div>
        <div className="mt-3 text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
          {lang === "de" ? "Unser Vorbereitungskurs für diesen Pfad ist in Planung" : "Our preparation course for this path is coming"}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {lang === "de"
            ? `Wir entwickeln gerade den Kurs für "${careerPathName}". Trag dich in die Warteliste ein und werde als erste:r informiert.`
            : `We are building the course for "${careerPathName}". Join the waitlist to be first in line.`}
        </p>
        {!submitted ? (
          <form onSubmit={onSubmit} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-md">
            <Input data-testid="waitlist-email" type="email" required placeholder={lang === "de" ? "du@firma.de" : "you@company.com"}
                   value={email} onChange={e => setEmail(e.target.value)} className="flex-1"/>
            <button data-testid="waitlist-submit" type="submit" disabled={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : (lang === "de" ? "Auf Warteliste" : "Join waitlist")}
            </button>
          </form>
        ) : (
          <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm" data-testid="waitlist-success">
            {lang === "de" ? "✓ Wir haben dich vorgemerkt. Du hörst von uns, sobald der Kurs live geht." : "✓ You're on the list. We'll notify you when the course goes live."}
          </div>
        )}
        <div className="mt-4">
          <button data-testid="waitlist-book-cta" onClick={() => openBooking("waitlist_alt")}
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline underline-offset-4">
            <Calendar className="h-4 w-4"/> {lang === "de" ? "Oder: Kostenloses Beratungsgespräch buchen" : "Or: Book a free consultation call"}
          </button>
        </div>
      </div>

      {relatedLivePaths.length > 0 && (
        <div className="rounded-xl border border-border p-5" data-testid="related-live-paths">
          <div className="text-[11px] uppercase tracking-[0.24em] text-accent">
            {lang === "de" ? "Verwandte Pfade mit sofort startbaren Kursen" : "Related paths with courses you can start today"}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3">
            {relatedLivePaths.map((p, i) => {
              const trial = p.kajabi_trial_offer_url;
              const landing = p.landing_url_status === "verified_live" && p.landing_url
                ? p.landing_url + `?utm_source=career_advisor&utm_campaign=${encodeURIComponent(p.career_path_id)}&utm_content=waitlist_alt`
                : null;
              const primary = trial || landing;
              return (
                <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{p.career_path_name}</div>
                    {p.product_name && <div className="text-xs text-muted-foreground truncate">{p.product_name}</div>}
                  </div>
                  {primary && (
                    <a href={primary} target="_blank" rel="noreferrer"
                       onClick={() => api.post(`/report/${token}/course-click`).catch(() => {})}
                       data-testid={`related-path-cta-${i}`}
                       className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:opacity-90 transition-opacity whitespace-nowrap">
                      {t("open_course")} <ExternalLink className="h-3 w-3"/>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* v2.7 — Product-level course card: kajabi_product_name is title, primary CTA = trial (fallback: UTM landing)
 * v2.17 — When course_product data is available (top.products from matching), render a rich card grid with
 *         cover images, dual CTAs (kostenlos testen + jetzt kaufen), and Framer landing details link. */
function ProductCourseCard({ course, cert, prepCourses, levelMilestones, careerPathId, products, token, courseHasLiveUrl, openBooking, t, lang }) {
  // v2.17 — new product grid rendering (image + dual CTA + details)
  if (products && products.length > 0) {
    return (
      <div className="space-y-4" data-testid="course-products-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {products.map((p, i) => {
            const utm = `?utm_source=career_advisor&utm_campaign=${encodeURIComponent(careerPathId || "")}&utm_content=${encodeURIComponent(p.id)}`;
            return (
              <div key={p.id || i} data-testid={`course-product-${i}`}
                   className="rounded-xl border border-border bg-card overflow-hidden hover:-translate-y-0.5 transition-transform flex flex-col">
                <div className="w-full aspect-video bg-muted flex items-center justify-center overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.title || ""} loading="lazy"
                         className="w-full h-full object-cover" data-testid={`course-product-image-${i}`}/>
                  ) : (
                    <GraduationCap className="h-10 w-10 text-muted-foreground/40"/>
                  )}
                </div>
                <div className="p-4 flex flex-col gap-3 flex-1">
                  <div>
                    <div className="text-sm font-semibold leading-snug" style={{ fontFamily: 'var(--font-heading)' }}
                         data-testid={`course-product-title-${i}`}>{p.title}</div>
                    {p.edition && (
                      <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                        {p.edition === "public" ? (lang === "de" ? "Public Cloud" : "Public Cloud") :
                         p.edition === "private" ? (lang === "de" ? "Private Edition" : "Private Edition") : p.edition}
                      </div>
                    )}
                  </div>
                  <div className="mt-auto flex flex-wrap gap-2 items-center">
                    {p.checkout_free_url && (
                      <a href={p.checkout_free_url + utm} target="_blank" rel="noreferrer"
                         onClick={() => api.post(`/report/${token}/course-click`, { product_id: p.id, cta: "free" }).catch(() => {})}
                         data-testid={`course-product-cta-free-${i}`}
                         className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:opacity-90 transition-opacity">
                        {lang === "de" ? "Kostenlos testen" : "Try for free"} <ExternalLink className="h-3.5 w-3.5"/>
                      </a>
                    )}
                    {p.checkout_full_url && (
                      <a href={p.checkout_full_url + utm} target="_blank" rel="noreferrer"
                         onClick={() => api.post(`/report/${token}/course-click`, { product_id: p.id, cta: "paid" }).catch(() => {})}
                         data-testid={`course-product-cta-paid-${i}`}
                         className="inline-flex items-center gap-1.5 rounded-full border border-primary text-primary px-4 py-2 text-xs font-semibold hover:bg-primary hover:text-primary-foreground transition-colors">
                        {lang === "de" ? "Jetzt kaufen" : "Buy now"}
                      </a>
                    )}
                    {p.landing_url && (
                      <a href={p.landing_url + utm} target="_blank" rel="noreferrer"
                         onClick={() => api.post(`/report/${token}/course-click`, { product_id: p.id, cta: "details" }).catch(() => {})}
                         data-testid={`course-product-cta-details-${i}`}
                         className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4">
                        {lang === "de" ? "Details" : "Details"} →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-muted-foreground">{t("free_line")}</div>
      </div>
    );
  }

  // legacy v2.7 rendering (used when no course_product mapped to this path)
  const productName = (course && course.kajabi_product_name) || (course && course.title) || (cert && cert.name) || "";
  const totalCourses = prepCourses.length || (course ? 1 : 0);
  const totalUnits = prepCourses.reduce((sum, c) => sum + (c.unit_count || 0), 0);
  const kuUnits = ((levelMilestones || {}).milestones || {}).key_user?.unit_count;
  const trialUrl = (course && course.kajabi_trial_offer_url) || (prepCourses.find(c => c.kajabi_trial_offer_url) || {}).kajabi_trial_offer_url;
  const landingLive = courseHasLiveUrl && course && course.landing_url;
  const utmLanding = landingLive
    ? course.landing_url + `?utm_source=career_advisor&utm_campaign=${encodeURIComponent(careerPathId || "")}`
    : null;
  const primaryHref = trialUrl || utmLanding;
  const primaryLabel = trialUrl ? t("open_course") : (lang === "de" ? "Zum Kurs" : "Open course");
  const secondaryHref = trialUrl && utmLanding ? utmLanding : null;
  return (
    <div className="flex flex-col sm:flex-row gap-5">
      <img src="https://images.pexels.com/photos/7394020/pexels-photo-7394020.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
           alt="" className="w-full sm:w-40 h-32 sm:h-32 rounded-lg object-cover border border-border shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }} data-testid="product-card-title">{productName}</div>
        {cert && cert.name && (
          <div className="mt-1 text-xs text-muted-foreground">
            <span className="uppercase tracking-widest text-[10px]">{t("prep_product_prepares")}</span>{" "}
            <span className="font-medium text-foreground">{cert.name}</span>
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {totalCourses > 0 && <Tag>{totalCourses} {t("prep_product_courses")}</Tag>}
          {totalUnits > 0 && <Tag>{totalUnits} {t("prep_product_units")}</Tag>}
          {kuUnits && <Tag>{lang === "de" ? `Key-User-Niveau ab ${kuUnits} Units` : `Key User level from ${kuUnits} units`}</Tag>}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{t("free_line")}</div>
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          {primaryHref ? (
            <a href={primaryHref} target="_blank" rel="noreferrer"
               onClick={() => api.post(`/report/${token}/course-click`).catch(() => {})}
               data-testid="course-cta-trial"
               className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
              {primaryLabel} <ExternalLink className="h-4 w-4"/>
            </a>
          ) : (
            <button data-testid="course-cta-fallback" onClick={() => openBooking("course_fallback")}
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
              <Calendar className="h-4 w-4"/>{t("cta_book_call")}
            </button>
          )}
          {secondaryHref && (
            <a href={secondaryHref} target="_blank" rel="noreferrer"
               data-testid="course-cta"
               className="text-sm text-primary hover:underline underline-offset-4">
              {t("prep_product_details")} →
            </a>
          )}
          {!primaryHref && (
            <span className="text-xs text-muted-foreground">{t("course_in_prep")}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function Fallback({ msg }) {
  return <div className="min-h-screen"><Brand /><div className="mx-auto max-w-2xl px-6 py-24 text-center text-muted-foreground">{msg}</div></div>;
}

/* v2.7 — Fixed 4-tile salary module: Key User / Consultant / Projektleiter / Freelancer */
function SalaryTiles({ tiles, t, lang }) {
  const [tracked, setTracked] = useState(false);
  useEffect(() => {
    if (!tiles || tiles.length === 0 || tracked) return;
    const el = document.getElementById("salary-progression");
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !tracked) {
          track("salary_progression_viewed", null, { tiles: tiles.length });
          setTracked(true);
        }
      });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [tiles, tracked]);
  if (!tiles || tiles.length === 0) return null;
  return (
    <section id="salary-progression" data-testid="s-progression" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
        <TrendingUp className="h-3.5 w-3.5"/> <span>{t("section_progression")}</span>
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((s, i) => (
          <motion.div key={s.key || i}
            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            data-testid={`salary-tile-${s.key}`}
            className="relative rounded-xl border border-border bg-card p-5 flex flex-col items-center text-center min-h-[170px]">
            <div className="absolute top-3 right-3 h-7 w-7 rounded-full bg-emerald-500/10 grid place-items-center">
              <ArrowUpRight className="h-4 w-4 text-emerald-600 dark:text-emerald-400"/>
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-accent">{lang === "de" ? s.label_de : s.label_en}</div>
            <div className="mt-3 text-2xl md:text-3xl font-semibold tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>
              {s.kind === "freelancer"
                ? (lang === "en"
                    ? (s.display_text_en || (s.hourly_rate_eur_min && s.hourly_rate_eur_max ? `€${s.hourly_rate_eur_min}–${s.hourly_rate_eur_max}/hr` : (s.display_text || "")))
                    : (s.display_text_de || s.display_text || ""))
                : (s.salary_max
                    ? (lang === "en"
                        ? `${t("salary_upto")} €${s.salary_max.toLocaleString("en-US")}`
                        : `${t("salary_upto")} ${s.salary_max.toLocaleString("de-DE")} €`)
                    : (lang === "de" ? "auf Anfrage" : "on request"))}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {lang === "de" ? s.exp_label_de : s.exp_label_en}
            </div>
          </motion.div>
        ))}
      </div>
      <div className="mt-4 text-[10px] text-muted-foreground italic">{t("salary_footnote")}</div>
    </section>
  );
}

/* v2.5 — Fixed 8-week roadmap from certification.roadmap_8_weeks (5 phases). AI never changes phase count. */
function Roadmap8({ phases, lang }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      {phases.map((p, i) => {
        const isOpen = open === i;
        const title = lang === "de" ? p.title_de : (p.title_en || p.title_de);
        return (
          <div key={i} className="border-b border-border last:border-b-0">
            <button
              data-testid={`roadmap8-toggle-${i}`}
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-muted/40 transition-colors">
              <div className="shrink-0 w-20 text-[10px] uppercase tracking-widest text-accent">{lang === "de" ? "Woche" : "Week"} {p.weeks}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{title}</div>
                {!isOpen && p.focus_de && <div className="mt-1 text-xs text-muted-foreground line-clamp-1">{lang === "de" ? p.focus_de : (p.focus_en || p.focus_de)}</div>}
              </div>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}/>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }} className="overflow-hidden">
                  <div className="px-5 pb-4 pl-[104px] text-sm text-muted-foreground space-y-1.5">
                    {p.focus_de && <div className="text-foreground">{lang === "de" ? p.focus_de : (p.focus_en || p.focus_de)}</div>}
                    {(p.activities || p.bullets || []).map((a, k) => (
                      <div key={k} className="flex gap-2"><span className="text-primary">›</span> {typeof a === "string" ? a : (a[lang] || a.de || a.en)}</div>
                    ))}
                    {p.exam_fee_note && <div className="mt-2 text-xs italic">{p.exam_fee_note}</div>}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

/* Salary progression tiles */
function SalaryProgression({ steps, t, lang }) {
  const [tracked, setTracked] = useState(false);
  useEffect(() => {
    if (!steps || steps.length === 0 || tracked) return;
    const el = document.getElementById("salary-progression");
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !tracked) {
          track("salary_progression_viewed", null, { steps: steps.length });
          setTracked(true);
        }
      });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [steps, tracked]);

  if (!steps || steps.length === 0) return null;

  return (
    <section id="salary-progression" data-testid="s-progression" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
        <TrendingUp className="h-3.5 w-3.5"/>
        <span>{t("section_progression")}</span>
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map((s, i) => (
          <motion.div key={s.role_id || i}
            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            data-testid={`prog-tile-${i}`}
            className={`relative rounded-xl border p-5 flex flex-col items-center text-center min-h-[160px] ${s.is_current ? "border-primary/50 bg-primary/5" : "border-border bg-card"}`}>
            <div className="absolute top-3 right-3 h-7 w-7 rounded-full bg-emerald-500/10 grid place-items-center">
              <ArrowUpRight className="h-4 w-4 text-emerald-600 dark:text-emerald-400"/>
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-accent">{s.is_current ? t("step_current") : (s.timespan_key ? t(s.timespan_key) : s.timespan_label)}</div>
            <div className="mt-3 text-3xl font-semibold tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>
              {s.salary_max
                ? (lang === "en"
                    ? `${t("salary_upto")} €${s.salary_max.toLocaleString("en-US")}`
                    : `${t("salary_upto")} ${s.salary_max.toLocaleString("de-DE")} €`)
                : t("step_no_salary")}
            </div>
            <div className="mt-2 text-xs text-muted-foreground line-clamp-2">
              <span className="text-foreground/80">{lang === "de" ? (s.title_de || s.title_en) : (s.title_en || s.title_de)}</span>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="mt-4 text-[10px] text-muted-foreground italic">{t("salary_footnote")}</div>
    </section>
  );
}


/* v2.14 — Report feedback thumbs (👍/👎) — stored on report + lead session */
function FeedbackThumbs({ token, lang, initial }) {
  const [feedback, setFeedback] = React.useState(initial || null);
  const [busy, setBusy] = React.useState(false);
  const submit = async (val) => {
    if (busy || feedback === val) return;
    setBusy(true);
    try {
      await api.post(`/report/${token}/feedback`, { feedback: val });
      setFeedback(val);
      track("report_feedback_given", null, { feedback: val });
    } catch {} finally { setBusy(false); }
  };
  const question = lang === "de" ? "War diese Empfehlung treffend?" : "Was this recommendation on point?";
  const thanks = lang === "de" ? "Danke für dein Feedback!" : "Thanks for your feedback!";
  return (
    <div className="rounded-xl border border-border bg-card p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="report-feedback">
      <div className="text-sm font-medium">{feedback ? thanks : question}</div>
      {!feedback && (
        <div className="flex items-center gap-2">
          <button data-testid="feedback-thumb-up" onClick={() => submit("positive")} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-emerald-500/10 hover:border-emerald-500/50 transition-colors disabled:opacity-60">
            👍 {lang === "de" ? "Ja, passt" : "Yes, fits"}
          </button>
          <button data-testid="feedback-thumb-down" onClick={() => submit("negative")} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-rose-500/10 hover:border-rose-500/50 transition-colors disabled:opacity-60">
            👎 {lang === "de" ? "Nicht ganz" : "Not quite"}
          </button>
        </div>
      )}
    </div>
  );
}
